'use client';

import { DollarSign, GraduationCap, Users, Wallet } from 'lucide-react';
import { SegmentDimensionEmptyState } from '@/domains/pages/ai-proficiency/components/segment-dimension-empty-state';
import { parseNoSegmentsEmptyReason } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import { NebulyPageEmptyState } from '@/domains/pages/components/nebuly-page-empty-state';
import { segmentKindLabel, type SegmentKind } from '@/domains/pages/shared/segment-kind';
import type { NebulyPageEmptyStateProps } from '@/domains/pages/types';
import { capitalizeFirstLetter } from '@/utils/capitalize-first-letter';
const AI_SPEND_SEGMENT_PAGE_KEYS: Record<string, SegmentKind> = {
  'ai-spend-team': 'team',
  'ai-spend-market': 'market',
  'ai-spend-tenure': 'tenure'
};
const AI_PROFICIENCY_SEGMENT_PAGE_KEYS: Record<string, SegmentKind> = {
  'ai-proficiency-team': 'team',
  'ai-proficiency-market': 'market',
  'ai-proficiency-tenure': 'tenure'
};
function buildAiProficiencySegmentEmptyCopy(kind: SegmentKind) {
  const noun = segmentKindLabel(kind);
  return {
    title: `No ${noun} data yet`,
    description: `${capitalizeFirstLetter(noun)} segment data is not available. Configure ${noun} ingestion to compare proficiency by ${noun}.`
  };
}
const AI_PROFICIENCY_SEGMENT_EMPTY_COPY = Object.fromEntries(Object.entries(AI_PROFICIENCY_SEGMENT_PAGE_KEYS).map(([pageKey, kind]) => [pageKey, buildAiProficiencySegmentEmptyCopy(kind)])) as Record<string, {
  title: string;
  description: string;
}>;
const AI_PROFICIENCY_SEGMENT_EMPTY_ICONS = Object.fromEntries(Object.keys(AI_PROFICIENCY_SEGMENT_PAGE_KEYS).map(pageKey => [pageKey, Users])) as Record<string, typeof GraduationCap>;
function buildAiSpendSegmentEmptyCopy(kind: SegmentKind) {
  const noun = segmentKindLabel(kind);
  return {
    title: `No ${noun} spend data yet`,
    description: `${capitalizeFirstLetter(noun)} segment spend data is not available. Configure ${noun} ingestion to compare AI spend by ${noun}.`
  };
}
const AI_SPEND_SEGMENT_EMPTY_COPY = Object.fromEntries(Object.entries(AI_SPEND_SEGMENT_PAGE_KEYS).map(([pageKey, kind]) => [pageKey, buildAiSpendSegmentEmptyCopy(kind)])) as Record<string, {
  title: string;
  description: string;
}>;
const AI_SPEND_SEGMENT_EMPTY_ICONS = Object.fromEntries(Object.keys(AI_SPEND_SEGMENT_PAGE_KEYS).map(pageKey => [pageKey, Users])) as Record<string, typeof GraduationCap>;
const EMPTY_COPY: Record<string, {
  title: string;
  description: string;
}> = {
  'ai-proficiency': {
    title: 'No proficiency data yet',
    description: 'AI Proficiency scores will appear here once enough interactions have been processed for the selected time range.'
  },
  team: {
    title: 'No team data yet',
    description: 'Team segment data is not available. Configure team ingestion to compare proficiency by team.'
  },
  market: {
    title: 'No market data yet',
    description: 'Market segment data is not available. Configure market ingestion to compare proficiency by market.'
  },
  tenure: {
    title: 'No tenure data yet',
    description: 'Tenure segment data is not available. Configure tenure ingestion to compare proficiency by tenure.'
  },
  upskilling: {
    title: 'No upskilling data yet',
    description: 'Upskilling recommendations will appear here once proficiency levels have been computed.'
  },
  'ai-spend': {
    title: 'No AI spend data yet',
    description: 'AI spend metrics will appear here once enough interactions with cost data have been processed for the selected time range.'
  },
  'model-savings': {
    title: 'No model savings data yet',
    description: 'Model savings opportunities will appear here once enough interaction and cost data has been processed for the last 30 days.'
  }
};
const EMPTY_ICONS: Record<string, typeof GraduationCap> = {
  'ai-proficiency': GraduationCap,
  upskilling: GraduationCap,
  'ai-spend': Wallet,
  'model-savings': DollarSign,
  team: Users,
  market: Users,
  tenure: Users
};
function createPageEmptyState(_pageKey: string) {
  return function PageEmptyState({
    pageKey: resolvedPageKey,
    reason
  }: NebulyPageEmptyStateProps) {
    const noSegmentsKind = parseNoSegmentsEmptyReason(reason);
    if (noSegmentsKind) {
      return <SegmentDimensionEmptyState pageKey={resolvedPageKey} reason={reason} report={resolvedPageKey === 'ai-spend' || resolvedPageKey.startsWith('ai-spend-') ? 'spend' : 'proficiency'} />;
    }
    const copy = EMPTY_COPY[resolvedPageKey] ?? AI_PROFICIENCY_SEGMENT_EMPTY_COPY[resolvedPageKey] ?? AI_SPEND_SEGMENT_EMPTY_COPY[resolvedPageKey] ?? {
      title: 'No data yet',
      description: 'Data for this page is not available.'
    };
    const Icon = EMPTY_ICONS[resolvedPageKey] ?? AI_PROFICIENCY_SEGMENT_EMPTY_ICONS[resolvedPageKey] ?? AI_SPEND_SEGMENT_EMPTY_ICONS[resolvedPageKey] ?? GraduationCap;
    return <NebulyPageEmptyState title={copy.title} description={reason ?? copy.description} icon={<Icon className="size-10 text-muted-foreground" strokeWidth={1.5} />} data-testid={`nebuly-page-empty-${resolvedPageKey}`} />;
  };
}
export const AiProficiencyPageEmptyState = createPageEmptyState('ai-proficiency');
export const TeamPageEmptyState = createPageEmptyState('team');
export const MarketPageEmptyState = createPageEmptyState('market');
export const TenurePageEmptyState = createPageEmptyState('tenure');
export const UpskillingPageEmptyState = createPageEmptyState('upskilling');
export const AiSpendPageEmptyState = createPageEmptyState('ai-spend');
export const AiSpendTeamPageEmptyState = createPageEmptyState('ai-spend-team');
export const AiSpendMarketPageEmptyState = createPageEmptyState('ai-spend-market');
export const AiSpendTenurePageEmptyState = createPageEmptyState('ai-spend-tenure');
export const ModelSavingsPageEmptyState = createPageEmptyState('model-savings');